import { useEffect, useState, type FormEvent } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { categories, site } from '@/mocks/guagua';

type NavItem = {
  label: string;
  to: string;
};

const navItems: NavItem[] = [
  { label: '首页', to: '/' },
  { label: '新瓜速递', to: '/news' },
  { label: '关于瓜哥', to: '/about' },
];

export default function SiteHeader() {
  const location = useLocation();
  const navigate = useNavigate();
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [catOpen, setCatOpen] = useState(false);
  const [keyword, setKeyword] = useState('');

  const isHome = location.pathname === '/';
  const transparent = isHome && !scrolled;

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    setMenuOpen(false);
    setCatOpen(false);
  }, [location.pathname, location.search]);

  const handleSearch = (event: FormEvent) => {
    event.preventDefault();
    const value = keyword.trim();
    if (!value) return;
    navigate(`/news?q=${encodeURIComponent(value)}`);
    setKeyword('');
  };

  const handleCategoryJump = (slug: string) => {
    setCatOpen(false);
    setMenuOpen(false);
    navigate(`/news?cat=${slug}`);
  };

  const headerTone = transparent
    ? 'text-background-50'
    : 'bg-background-50/95 backdrop-blur border-b border-background-200 text-foreground-950';

  return (
    <header className={`fixed inset-x-0 top-0 z-50 transition-colors duration-300 ${headerTone}`}>
      <div className="flex items-center justify-between h-16 md:h-20 px-4 md:px-6">
        <Link to="/" className="flex items-center gap-2 shrink-0" aria-label={`${site.name} 首页`}>
          <span
            className={`flex items-center justify-center w-9 h-9 md:w-10 md:h-10 rounded-md text-lg ${
              transparent ? 'bg-background-50/20 text-background-50' : 'bg-primary-500 text-background-50'
            }`}
          >
            <i className="ri-emotion-laugh-line" aria-hidden="true"></i>
          </span>
          <span className="flex flex-col leading-none">
            <span className="font-logo text-lg md:text-xl tracking-wide">{site.name}</span>
            <span
              className={`hidden sm:block text-[10px] tracking-widest ${
                transparent ? 'text-background-200' : 'text-foreground-500'
              }`}
            >
              GUAGE CHIGUA
            </span>
          </span>
        </Link>

        <nav className="hidden md:flex items-center gap-1" aria-label="主导航">
          {navItems.map((item) => {
            const active = location.pathname === item.to;
            return (
              <Link
                key={item.to}
                to={item.to}
                className={`px-3 py-2 rounded-md text-sm font-medium whitespace-nowrap transition-colors ${
                  transparent
                    ? 'hover:bg-background-50/15'
                    : active
                      ? 'bg-primary-100 text-primary-700'
                      : 'text-foreground-700 hover:bg-background-100'
                }`}
              >
                {item.label}
              </Link>
            );
          })}

          <div className="relative">
            <button
              type="button"
              onClick={() => setCatOpen((prev) => !prev)}
              className={`flex items-center gap-1 px-3 py-2 rounded-md text-sm font-medium whitespace-nowrap transition-colors ${
                transparent ? 'hover:bg-background-50/15' : 'text-foreground-700 hover:bg-background-100'
              }`}
              aria-expanded={catOpen}
            >
              热门分类
              <i className={`ri-arrow-down-s-line transition-transform ${catOpen ? 'rotate-180' : ''}`} aria-hidden="true"></i>
            </button>
            {catOpen && (
              <div className="absolute right-0 mt-2 w-64 grid grid-cols-2 gap-1 p-2 rounded-lg bg-background-50 border border-background-200 shadow-sm text-foreground-950">
                {categories.map((item) => (
                  <button
                    key={item.slug}
                    type="button"
                    onClick={() => handleCategoryJump(item.slug)}
                    className="flex items-center gap-2 px-3 py-2 rounded-md text-sm text-left text-foreground-700 hover:bg-background-100 cursor-pointer"
                  >
                    <span className="flex items-center justify-center w-6 h-6 text-primary-600">
                      <i className={item.icon} aria-hidden="true"></i>
                    </span>
                    {item.name}
                  </button>
                ))}
              </div>
            )}
          </div>
        </nav>

        <div className="flex items-center gap-2">
          <form onSubmit={handleSearch} className="hidden lg:flex items-center" role="search">
            <div className="relative">
              <i
                className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-foreground-400 text-sm"
                aria-hidden="true"
              ></i>
              <input
                type="search"
                value={keyword}
                onChange={(event) => setKeyword(event.target.value)}
                placeholder="搜你想吃的瓜"
                aria-label="搜索内容"
                className="w-48 xl:w-56 pl-9 pr-3 py-2 rounded-full text-sm bg-background-100 border border-background-200 text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-300"
              />
            </div>
          </form>

          <Link
            to="/news"
            className="hidden sm:inline-flex items-center gap-1 px-4 py-2 rounded-md text-sm font-medium whitespace-nowrap bg-primary-500 text-background-50 hover:bg-primary-600 transition-colors"
          >
            <i className="ri-fire-line" aria-hidden="true"></i>
            最新更新
          </Link>

          <button
            type="button"
            onClick={() => setMenuOpen((prev) => !prev)}
            className={`md:hidden flex items-center justify-center w-10 h-10 rounded-md ${
              transparent ? 'text-background-50' : 'text-foreground-900'
            }`}
            aria-label="打开菜单"
            aria-expanded={menuOpen}
          >
            <i className={menuOpen ? 'ri-close-line text-xl' : 'ri-menu-line text-xl'} aria-hidden="true"></i>
          </button>
        </div>
      </div>

      {menuOpen && (
        <div className="md:hidden bg-background-50 border-t border-background-200 px-4 pb-4 pt-2 text-foreground-950">
          <form onSubmit={handleSearch} className="lg:hidden mb-3" role="search">
            <div className="relative">
              <i
                className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-foreground-400 text-sm"
                aria-hidden="true"
              ></i>
              <input
                type="search"
                value={keyword}
                onChange={(event) => setKeyword(event.target.value)}
                placeholder="搜你想吃的瓜"
                aria-label="搜索内容"
                className="w-full pl-9 pr-3 py-2 rounded-md text-sm bg-background-100 border border-background-200 text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-300"
              />
            </div>
          </form>
          <div className="flex flex-col">
            {navItems.map((item) => (
              <Link
                key={item.to}
                to={item.to}
                className="px-3 py-3 rounded-md text-sm font-medium text-foreground-700 hover:bg-background-100"
              >
                {item.label}
              </Link>
            ))}
          </div>
          <p className="px-3 pt-3 pb-1 text-xs text-foreground-500">热门分类</p>
          <div className="grid grid-cols-2 gap-1">
            {categories.map((item) => (
              <button
                key={item.slug}
                type="button"
                onClick={() => handleCategoryJump(item.slug)}
                className="flex items-center gap-2 px-3 py-2 rounded-md text-sm text-left text-foreground-700 hover:bg-background-100 cursor-pointer"
              >
                <i className={item.icon} aria-hidden="true"></i>
                {item.name}
              </button>
            ))}
          </div>
        </div>
      )}
    </header>
  );
}